<template>
  <div class="input-box" :class="{focus, disabled, 'secondary' : secondaryStyle, 'is-floating': floatingLabel}">
    <span class="input-box__icon input-icon" v-if="icon">
      <vita-icon :name="icon"/>
    </span>
    <label
      v-if="placeholder && floatingLabelComputed"
      :class="{ active: focus || modelValue.length }"
    >
      {{ placeholder }}
    </label>
    <input
      ref="inputRef"
      :name="inputName"
      :type="inputType"
      :value="modelValue"
      :placeholder="floatingLabelComputed ? '' : placeholder"
      @focus="focusInput(true)"
      @blur="focusInput(false)"
      @input="handleChange"
      :disabled="disabled"
    />
    <span class="input-box__icon password-icon" v-if="type === 'password'" @click="showPassword"
          data-qa="input-icon-toggle-password">
      <vita-icon v-if="modelValue.length" :name="passwordIcon ? 'eye' : 'open-eye'"/>
    </span>
    <div class="input-box__error" v-if="error">{{error}}</div>
  </div>
</template>

<script lang="ts" setup>
import { ref, watch, onBeforeMount, computed } from 'vue';

interface Props {
  placeholder?: string
  modelValue: string
  type: string
  mask?: string
  error?: string
  icon?: string
  disabled?: boolean
  secondaryStyle?: string
  inputName?: string
  floatingLabel?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  type: 'text',
  floatingLabel: false
})

const focus = ref(false)
const inputRef = ref<HTMLInputElement | null>(null); // Ref для внутреннего input

const emit = defineEmits(['update:modelValue']);

const passwordIcon = ref(true);
const inputType = ref('text');
const showPassword = () => {
  passwordIcon.value = !passwordIcon.value
}

onBeforeMount(() => {
  inputType.value = props.type
})

const handleChange = (event: Event) => {
  const newValue = (event.target as HTMLInputElement).value;
  emit('update:modelValue', newValue);
};

const focusInput = (i:boolean) => {
  focus.value = i
}

watch(passwordIcon, (val: boolean) => {
  inputType.value = !val ? 'text' : 'password'
});

// Если у поля есть иконка, плавающий label отключается
const floatingLabelComputed = computed(() => props.floatingLabel && !props.icon);

// Экспортируем метод focus
defineExpose({
  focus: () => {
    if (inputRef.value) {
      inputRef.value.focus();
    }
  }
});
</script>


<script lang="ts">
export default {
  name: 'vita-input'
}
</script>

<style scoped lang="scss">
@use '../../static/style/variables.scss' as *;
.input-box {
  margin-bottom: 20px;
  width: 100%;
  display: flex;
  font-family: initial;
  height: 40px;
  border: 1px solid #CCCCCC;
  box-shadow: 1px 1px 5px 0 #00000014;
  font-size: 15px;
  font-weight: 400;
  color: #BBBBBB;
  border-radius: 3px;
  position: relative;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0, 0, 0.2, 1);

  &.is-floating {
    overflow: visible;
  }

  &.no-shadow,
  &.no-shadow.focus {
    box-shadow: unset;
  }

  &:not(:has(.input-icon)) {
    padding-left: 14px;
    padding-right: 14px;
  }

  &:has(.password-icon) {
    padding-right: 0;
  }

  &.focus {
    border: 1px solid #30A8FF;
    box-shadow: 0 0 0 2px #CBE9FF;

    .active {
      color: #30A8FF;
    }
  }

  &:hover {
    border-color: #30A8FF;
  }

  label {
    font-family: "Montserrat", Helvetica, Arial, sans-serif;
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    transition: all 0.2s cubic-bezier(0, 0, 0.2, 1);
    color: #BBBBBB;
    pointer-events: none;
    font-size: 15px;
    font-weight: 400;
    line-height: 18.29px;
    text-align: left;
    text-underline-position: from-font;
    text-decoration-skip-ink: none;
  }

  .active {
    top: -9px;
    left: 0;
    font-size: 12px;
    color: #CCCCCC;
    font-weight: 500;
    line-height: 14.63px;
  }

  &__icon {
    width: 44px;
    display: flex;
    background: #FFFFFF;

    svg {
      margin: auto;
    }
  }

  .password-icon {
    cursor: pointer;
  }

  &__error {
    color: $red;
    font-size: 12px;
    text-align: center;
    width: 100%;
    left: 0;
    position: absolute;
    bottom: -18px;
  }

  input {
    border: 0;
    height: 100%;
    color: $text-color;
    font-size: 15px;
    width: 100%;
    font-family: "Montserrat", Helvetica, Arial, sans-serif;
    &::-webkit-calendar-picker-indicator {
      display: none;
      -webkit-appearance: none;
    }
    &::-webkit-inner-spin-button {
      display: none;
      -webkit-appearance: none;
    }
    &::placeholder {
      color: $text-color-light;
      font-size: 15px;
    }

    &[type="password"] {
      font-size: 15px;
      letter-spacing: 0.2em;

      &::placeholder {
        letter-spacing: 0;
      }
    }
  }
}

[readonly] input {
  background: transparent;
  pointer-events: none;
  color: rgba(136, 136, 136, 1);
}

.secondary input {
  border: 1px solid transparent;
  border-radius: 3px;
  transition: .15s ease;

  &:hover {
    border: 1px solid #30A8FF;
  }

  &:focus {
    border: 1px solid #30A8FF;
    box-shadow: 0 0 0 2px #CBE9FF;
  }
}
</style>
